import { View, Pill, Spinner, Flex, Text } from '@smarterservices/smarter-ui';
import { Case } from 'change-case-all';
import {
  getStore,
  usePlayerStore,
} from '../../../stores/StoreContext';
import Webrtc from './Webrtc';
import Hls from './Hls';
import { memo, useState, useCallback, useEffect } from 'react';
import './VideoContainer.css';
import Disconnected from './Disconnected';
import NotYetConnected from './NotYetConnected';
import CONSTANTS from '../../../config/constants';

const VideoContainer = ({
  SessionTrack,
  isFocusedTrack = false,
  trackStatus = 'connected', // can be connected, disconnected, or null
  width = '250px',
  height = '250px',
}) => {
  const PlayerStore = getStore('playerStore');
  const activeVideoSource = usePlayerStore((state) => state.activeVideoSource);
  const displayVideoLabels = usePlayerStore((state) => state.displayVideoLabels);

  // Track WebRTC connection status separately for live sessions
  // Start with 'loading' to show loading state during initial load
  const [webrtcTrackStatus, setWebrtcTrackStatus] = useState('loading');

  // Callback for WebRTC component to report track status changes
  const handleWebrtcTrackStatusChange = useCallback((status) => {
    setWebrtcTrackStatus(status);
  }, [SessionTrack.id, SessionTrack.name, isFocusedTrack]);

  // Reset webrtcTrackStatus when SessionTrack changes (e.g., reconnection with new track)
  useEffect(() => {
    if (activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) {
      setWebrtcTrackStatus('loading');
    }
  }, [SessionTrack?.id, SessionTrack?.lastUpdated, activeVideoSource]);

  /**
   * Determines the effective status for HLS (recorded) tracks.
   * Uses the trackStatus prop which comes from getTrackStatusAtTime in the parent component.
   * 
   * @returns {string|null} 'connected', 'disconnected', or null
   */
  const getEffectiveHlsStatus = () => {
    // For HLS, we rely on the trackStatus prop which is calculated by getTrackStatusAtTime
    // based on the track's status history at the current playback time
    return trackStatus;
  };

  /**
   * Determines the effective status for WebRTC (live) tracks.
   * Uses the webrtcTrackStatus state which is updated by the Webrtc component
   * based on LiveKit subscription status.
   * 
   * @returns {string} 'connected', 'disconnected', or 'loading'
   */
  const getEffectiveWebRtcStatus = () => {
    // While loading, show as 'connected' to display the video container
    // The Webrtc component will handle showing loading state if needed
    if (webrtcTrackStatus === 'loading') {
      return 'connected';
    }

    // Return the actual WebRTC status (connected/disconnected)
    return webrtcTrackStatus;
  };

  // Determine effective track status based on video source
  const effectiveTrackStatus = activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE
    ? getEffectiveWebRtcStatus()
    : getEffectiveHlsStatus();

  // Determine if clicking should be allowed
  // Only allow clicking on connected tracks that aren't already focused
  const allowClick = !isFocusedTrack && effectiveTrackStatus === 'connected';

  if (!SessionTrack) return null;

  const handleClick = () => {
    // Only change focus if this isn't already the focused track
    if (PlayerStore.focusedTrackId !== SessionTrack.id) {
      PlayerStore.focusTrack(SessionTrack.id);
    }
  };

  return (
    <View
      as="div"
      position="relative"
      className="video-container"
      background='brand'
      withVisualDebug={false}
      cursor={allowClick ? 'pointer' : 'default'}
      onClick={allowClick ? handleClick : null}
      width={width}
      height={height}
      themeOverride={{
        backgroundBrand: '#2e3c46'
      }}

    >
      {/* Show video if status is connected, disconnected, or not-yet-connected */}
      {(effectiveTrackStatus === 'connected') && (
        <>
          {activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE ? (
            // Key includes lastUpdated timestamp to force component remount on reconnection
            // This ensures video elements properly re-attach to new LiveKit tracks after disconnection
            <Webrtc
              key={`webrtc_${SessionTrack.id}_${SessionTrack.lastUpdated || 0}`}
              SessionTrack={SessionTrack}
              onTrackStatusChange={handleWebrtcTrackStatusChange}
            />
          ) : (
            <Hls
              key={`hls_${SessionTrack.id}`}
              SessionTrack={SessionTrack}
              isFocusedTrack={isFocusedTrack}
              width={width}
              height={height}
            />
          )}
        </>
      )}

      {/* Show disconnected overlay on top of video if disconnected */}
      {effectiveTrackStatus === 'disconnected' && <Disconnected SessionTrack={SessionTrack} />}

      {/* Show not-yet-connected overlay for tracks that haven't connected yet */}
      {effectiveTrackStatus === 'not-yet-connected' && <NotYetConnected SessionTrack={SessionTrack} />}

      {displayVideoLabels === 'show' && (
        <View as="div" className="video-label">
          <Pill
            color="primary"
            className="video-label"
            themeOverride={{
              background: 'rgba(0,0,0,1)',
              primaryColor: 'rgb(230, 230, 230)',
              textFontSize: '10px',
            }}
          >
            {Case.capital(SessionTrack.name)}
          </Pill>
        </View>
      )}
    </View>
  );
};

// Memoize the VideoContainer component to prevent unnecessary re-renders
const MemoizedVideoContainer = memo(VideoContainer, (prevProps, nextProps) => {
  // Return true if passing nextProps to render would return
  // the same result as passing prevProps to render,
  // otherwise return false
  return (
    prevProps.SessionTrack?.id === nextProps.SessionTrack?.id &&
    prevProps.SessionTrack?.lastUpdated === nextProps.SessionTrack?.lastUpdated &&
    prevProps.isFocusedTrack === nextProps.isFocusedTrack &&
    prevProps.trackStatus === nextProps.trackStatus &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.magnification?.enabled === nextProps.magnification?.enabled &&
    prevProps.magnification?.level === nextProps.magnification?.level &&
    prevProps.magnification?.lensSize === nextProps.magnification?.lensSize
  );
});

export default MemoizedVideoContainer;
